// Public-facing error + connection copy for the KA Intelligence chamber.
//
// Two rules: the visitor always sees something actionable, and the copy never
// leaks developer jargon, provider names, model ids or stack detail.

export type ConnectionTone = "ready" | "working" | "capacity" | "unavailable" | "error";

export interface ConnectionState {
  tone: ConnectionTone;
  label: string;
}

const GENERIC = "Something interrupted the answer. Please try again.";

function looksTechnical(text: string): boolean {
  return (
    text.length === 0 ||
    text.length > 400 ||
    /<\/?[a-z][\s\S]*>/i.test(text) ||
    /\b(stack|TypeError|ReferenceError|undefined is not|at Object\.|Error:)\b/.test(text)
  );
}

/**
 * Turn a non-2xx API response into a safe sentence for the conversation area.
 * The server's own message is preferred when it is short and human.
 */
export function publicErrorMessage(status: number, body: string): string {
  const trimmed = (body ?? "").trim();
  const safe = looksTechnical(trimmed) ? "" : trimmed;
  if (safe) return safe;
  switch (status) {
    case 413:
      return "That message is too long for one turn. Shorten it and send again.";
    case 429:
      return "You've reached the beta limit for this hour. Rest a moment and return.";
    case 499:
      return "The answer was stopped.";
    case 503:
      return "KA Intelligence is not available right now. Please try again shortly.";
    case 502:
      return "KA Intelligence could not answer right now. Please try again.";
    default:
      return GENERIC;
  }
}

/** Restrained health line rendered beside the composer. */
export function connectionState(input: {
  busy: boolean;
  status: string;
  errorMessage: string | null;
}): ConnectionState {
  const msg = input.errorMessage ?? "";
  if (/beta limit|limit for this hour/i.test(msg)) return { tone: "capacity", label: "Capacity reached" };
  if (/not available right now|not configured/i.test(msg))
    return { tone: "unavailable", label: "Provider unavailable" };
  if (msg) return { tone: "error", label: "Interrupted" };
  if (input.busy) return { tone: "working", label: input.status === "submitted" ? "Sending" : "Working" };
  return { tone: "ready", label: "Ready" };
}
